import { useMemo, useCallback } from 'react';
import { AccountBase } from '@/models/AccountViewModel';
import { useGetTabSettingsQuery } from '@/store/tabsApi';
import HoldingsTable from '@/modules/portfolio/tables/holdingsTable';
import AssetAllocationPieChart from '@/modules/portfolio/charts/assetAllocationPieChart';
import DocumentVault from '@/pages/document/documentVault';
import GainLoss from '@/modules/portfolio/gainLoss/GainLoss';
import Permissions from '@/components/permissions';
import AccountsHistoricalGrid from '@/components/grids/historical/AccountsHistoricalGrid'; // Placeholder: Create this component
import dayjs from '@/config/dayjs';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import Notes from '@/pages/document/notes';
import { Typography } from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';
import { useShowAllInCashFlow } from '@/hooks/useShowAllInCashFlow';
import { useModuleAccess } from '@/permissions';
import AccountBaseUtility from '@/utils/accountBase.utility';
import { AccountTypeNumber } from '@/utils/enums';
import { API_DATE_FORMAT, formatAppDate } from '@/utils/date';
import { TransactionViewRouter } from '@/components/flyouts/AccountDetailsFlyout/TransactionViewRouter';
import type { TransactionPeriod } from '@/components/flyouts/AccountDetailsFlyout/transactionPeriod';

export interface Tab {
  id: string;
  title: string;
  component: React.ReactNode;
}

/**
 * The account flyout's tabs. `transactionPeriod` is the range the transactions
 * tab shows; without one it shows the month so far, as legacy did.
 */
export const useTabs = (
  account: AccountBase | undefined,
  tabsPanelKey: string = 'investor-account',
  investorId?: number,
  transactionPeriod?: TransactionPeriod,
) => {
  const { data: tabSettings } = useGetTabSettingsQuery(tabsPanelKey);
  const { t } = useLocalization();

  const { validateByModule } = useModuleAccess();
  const showAllInCashFlow = useShowAllInCashFlow();

  const getTransactionTitle = useCallback(() => {
    return showAllInCashFlow
      ? t('Cash Flow')
      : t('transactions.label', 'Transactions');
  }, [showAllInCashFlow, t]);

  const canShowTransactions = useCallback(
    (account: AccountBase) => {
      const isAllocation = AccountBaseUtility.isAllocation(account);
      const isBanking = AccountBaseUtility.isBanking(account);

      return (
        !isAllocation &&
        (isBanking
          ? validateByModule('CashFlow')
          : validateByModule('TradingActivity'))
      );
    },
    [validateByModule],
  );

  const canShowHoldings = (account: AccountBase) => {
    const isClosed = AccountBaseUtility.isClosed(account);
    const isOffline = AccountBaseUtility.isOffline(account);
    const isAsset = account.IsAsset;

    return !isClosed && !isOffline && isAsset;
  };

  const canShowGainsAndLosses = useCallback(
    (account: AccountBase) => {
      const isAllocation = AccountBaseUtility.isAllocation(account);
      const hasTaxLots = account.HasTaxLots;

      return !isAllocation && hasTaxLots && validateByModule('Gains&Losses');
    },
    [validateByModule],
  );

  // TODO: https://wealthaccess.atlassian.net/browse/NU-1194
  const canShowDocuments = () => {
    return true;
  };

  const canShowPermissions = (account: AccountBase) => {
    const isAllocation = AccountBaseUtility.isAllocation(account);

    return (
      !isAllocation && account.AccountType !== AccountTypeNumber.ADVISOR_MANAGED
    );
  };

  const baseTabs = useMemo(() => {
    if (!account) return [];
    const dateFilter = DataRangeFilterEnum.OpenYear;
    const dateRange = getDateRangeFromFilter(dateFilter);
    const startDate = formatAppDate(dateRange.startDate, {
      format: API_DATE_FORMAT,
    });
    const endDate = formatAppDate(dateRange.endDate, {
      format: API_DATE_FORMAT,
    });

    const baseTabs: (Tab & { isEnabled: boolean })[] = [
      {
        id: 'holdings',
        title: 'Holdings',
        component: (
          <HoldingsTable
            isWidget={false}
            endDate={dayjs().format(API_DATE_FORMAT)}
            accountId={account.AccountId}
            size='flyout'
          />
        ),
        isEnabled: canShowHoldings(account),
      },
      {
        id: 'historical',
        title: 'Historical Values',
        component: (
          <AccountsHistoricalGrid
            accountId={account.AccountId}
            liveBalance={account.Balance}
            dateFilter={dateFilter}
            startDate={startDate}
            endDate={endDate}
          />
        ),
        isEnabled: true,
      },
      {
        id: 'pd',
        title: t('Asset Allocation'),
        component: (
          <AssetAllocationPieChart
            accountId={account.AccountId}
            asOfDate={new Date(endDate)}
          />
        ),
        isEnabled: true,
      },
      {
        id: 'taxlots',
        title: 'Gains & Losses',
        component: (
          <GainLoss
            isWidget
            container='flyout'
            accountId={account.AccountId}
            investorId={investorId}
          />
        ),
        isEnabled: canShowGainsAndLosses(account),
      },
      {
        id: 'transactions',
        title: getTransactionTitle(),
        component: (
          <TransactionViewRouter
            account={account}
            investorId={investorId}
            period={transactionPeriod}
          />
        ),
        isEnabled: canShowTransactions(account),
      },
      {
        id: 'vault',
        title: 'Documents',
        component: (
          <DocumentVault
            wealthAccountId={account.AccountId}
            investorId={investorId}
          />
        ),
        isEnabled: canShowDocuments(),
      },
      {
        id: 'notes',
        title: 'Notes',
        component: account.Memo ? (
          <Notes memo={account.Memo} />
        ) : (
          <Typography color='text.secondary'>No notes entered</Typography>
        ),
        isEnabled: true,
      },
      {
        id: 'permissions',
        title: 'Permissions',
        component: <Permissions accountId={account.AccountId} />,
        isEnabled: canShowPermissions(account), // Simplified from legacy `!isAdvisorLoaded`
      },
    ];

    return baseTabs;
  }, [
    account,
    canShowGainsAndLosses,
    canShowTransactions,
    getTransactionTitle,
    investorId,
    t,
    transactionPeriod,
  ]);

  const allTabs = useMemo(() => {
    let enabledTabs = baseTabs.filter((tab) => tab.isEnabled);

    if (tabSettings) {
      // Hide tabs specified by the API
      const hiddenIds = new Set(tabSettings.hidden || []);
      enabledTabs = enabledTabs.filter((tab) => !hiddenIds.has(tab.id));

      // Order tabs based on the API's order array
      if (tabSettings.order && tabSettings.order.length > 0) {
        const orderMap = new Map(
          tabSettings.order.map((id, index) => [id, index]),
        );
        enabledTabs.sort((a, b) => {
          const aOrder = orderMap.get(a.id) ?? Infinity;
          const bOrder = orderMap.get(b.id) ?? Infinity;
          return aOrder - bOrder;
        });
      }
    }

    return enabledTabs;
  }, [baseTabs, tabSettings]);

  return {
    tabs: allTabs,
    // Set the default tab to be the first one in the final list
    defaultTab: allTabs.length > 0 ? allTabs[0] : undefined,
  };
};
